// useFormStatePreservation
// ----------------------------------------------------------------------------
// Phase 2 of the stale-tab version-check RCA (2026-06-08).
//
// Why this exists:
//   Operators avoid clicking the version-check "Refresh" toast because a hard
//   reload wipes any unsubmitted form state (booking wizard, follow-up notes,
//   create-lead). This hook persists React Hook Form values (or any shallow
//   form object) into sessionStorage so a forced reload doesn't lose work.
//
// Usage:
//   const { restore, save, clear } = useFormStatePreservation(
//     'v2-book-step2',           // stable dialog id
//     leadId,                    // namespaced per record so two open leads don't collide
//     { skip: !open }            // optional — don't read/write when dialog closed
//   );
//
//   // RHF integration:
//   const persisted = restore();
//   const methods = useForm({ defaultValues: { ...defaults, ...persisted } });
//   useEffect(() => { save(methods.watch()); }, [values, save]);
//   // On success: clear();
//
// Storage shape: sessionStorage[`formState:<dialogId>:<leadId|none>`] = {
//   v: 1,                         // schema version
//   t: <ms since epoch>,          // for TTL
//   d: <values>,                  // the form snapshot
// }
//
// Constraints:
//   - 50KB per-entry size cap (defensive against accidental blob fields)
//   - 24h TTL — restore() rejects + clears anything older
//   - Sanitizes Date / moment objects on save; re-hydrates ISO strings to Date
//     when serialized as a tagged marker.
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef } from 'react';

const SCHEMA_VERSION = 1;
const TTL_MS = 24 * 60 * 60 * 1000;          // 24h
const MAX_BYTES = 50 * 1024;                  // 50KB
const DEFAULT_DEBOUNCE_MS = 500;

const safeSessionStorage = () => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return null;
        // sniff-test (Safari private mode throws on setItem)
        const probe = '__fsp_probe__';
        window.sessionStorage.setItem(probe, '1');
        window.sessionStorage.removeItem(probe);
        return window.sessionStorage;
    } catch {
        return null;
    }
};

const buildKey = (dialogId, recordId) => {
    const safeDialog = String(dialogId || 'unknown').replace(/[^a-zA-Z0-9._-]/g, '_');
    const safeRecord = recordId == null || recordId === '' ? 'none' : String(recordId);
    return `formState:${safeDialog}:${safeRecord}`;
};

// JSON.stringify replacer — converts Date / moment to a tagged marker so
// restore can re-hydrate.
const replacer = (_key, value) => {
    if (value instanceof Date) return { __t: 'Date', v: value.toISOString() };
    if (value && typeof value === 'object' && value._isAMomentObject && typeof value.toISOString === 'function') {
        return { __t: 'Date', v: value.toISOString() };
    }
    return value;
};

const reviver = (_key, value) => {
    if (value && typeof value === 'object' && value.__t === 'Date' && typeof value.v === 'string') {
        const d = new Date(value.v);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return value;
};

export default function useFormStatePreservation(dialogId, recordId, options = {}) {
    const { skip = false, debounceMs = DEFAULT_DEBOUNCE_MS } = options;
    const storage = useMemo(safeSessionStorage, []);
    const key = useMemo(() => buildKey(dialogId, recordId), [dialogId, recordId]);
    const timerRef = useRef(null);
    const lastSerializedRef = useRef(null);

    // ── restore ──────────────────────────────────────────────────────────────
    // Returns the persisted values object, or null when nothing usable.
    const restore = useCallback(() => {
        if (skip || !storage) return null;
        try {
            const raw = storage.getItem(key);
            if (!raw) return null;
            const parsed = JSON.parse(raw, reviver);
            if (!parsed || parsed.v !== SCHEMA_VERSION) {
                storage.removeItem(key);
                return null;
            }
            if (typeof parsed.t !== 'number' || Date.now() - parsed.t > TTL_MS) {
                storage.removeItem(key);
                return null;
            }
            return parsed.d || null;
        } catch {
            // corrupt entry — wipe it
            try { storage.removeItem(key); } catch { /* noop */ }
            return null;
        }
    }, [key, skip, storage]);

    // ── save (debounced) ──────────────────────────────────────────────────────
    const save = useCallback((values) => {
        if (skip || !storage) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            try {
                if (values == null || typeof values !== 'object') return;
                const serialized = JSON.stringify({ v: SCHEMA_VERSION, t: Date.now(), d: values }, replacer);
                if (serialized.length > MAX_BYTES) {
                    // refuse abnormally large payloads — better to lose one
                    // oversized form than to fill the sessionStorage quota
                    // and break other dialogs' persistence too.
                    return;
                }
                // skip identical writes — avoids quota churn when watch() fires
                // on focus/blur without underlying value change
                if (serialized === lastSerializedRef.current) return;
                lastSerializedRef.current = serialized;
                storage.setItem(key, serialized);
            } catch {
                /* quota or serialization error — silently skip */
            }
        }, debounceMs);
    }, [debounceMs, key, skip, storage]);

    // ── clear ────────────────────────────────────────────────────────────────
    const clear = useCallback(() => {
        if (!storage) return;
        try { storage.removeItem(key); } catch { /* noop */ }
        if (timerRef.current) clearTimeout(timerRef.current);
        lastSerializedRef.current = null;
    }, [key, storage]);

    // Cancel any pending debounced write on unmount — dropping the timer is
    // safe because reload races already bound user loss to debounceMs.
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    return { restore, save, clear };
}
